/**
 * Types and pure helpers for PlanAssignmentModal (create_invitation flow).
 * Keeps validation and payload construction testable and out of the component.
 */

import { Plan } from '../../models/plan';

export type WorkspaceMode = 'new_workspace' | 'existing_workspace' | 'custom_workspace';

export type PlanType = 'fixed' | 'payg' | 'enterprise';

export interface InvitationFormState {
  email: string;
  workspaceMode: WorkspaceMode;
  tenantId: string | null;
  tenantName: string;
  planType: PlanType;
  fixedPlanCode: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: {
    email?: string;
    tenantId?: string;
    tenantName?: string;
    planType?: string;
    planId?: string;
  };
}

/** Minimal payload for invitation API (snake_case). */
export interface InvitationPayload {
  email: string;
  assigned_plan_code?: string;
  tenant_id?: string;
  tenant_name?: string;
}

/**
 * Resolve assigned_plan_code from form state and available plans.
 * Used for new_workspace and custom_workspace.
 */
function resolveAssignedPlanCode(
  planType: PlanType,
  fixedPlanCode: string,
  plans: Plan[]
): string | undefined {
  if (planType === 'fixed') {
    return fixedPlanCode.trim() || undefined;
  }
  if (planType === 'payg') {
    const payg = plans.find((p) => p.plan_kind === 'payg');
    return payg?.plan_code;
  }
  if (planType === 'enterprise') {
    const custom =
      plans.find((p) => p.plan_kind === 'custom') ??
      plans.find((p) => p.plan_code?.toLowerCase() === 'enterprise');
    return custom?.plan_code;
  }
  return undefined;
}

/**
 * Validate invitation form for the active workspace mode.
 * Returns valid: true only when all required fields for that mode are satisfied.
 */
export function validateInvitationForm(
  mode: WorkspaceMode,
  state: InvitationFormState,
  plans: Plan[]
): ValidationResult {
  const errors: ValidationResult['errors'] = {};
  const emailTrim = state.email.trim();

  if (!emailTrim) {
    errors.email = 'Email is required';
  }

  if (mode === 'new_workspace') {
    const planCode = resolveAssignedPlanCode(state.planType, state.fixedPlanCode, plans);
    if (!state.planType) {
      errors.planType = 'Select a plan type';
    } else if (!planCode) {
      if (state.planType === 'fixed') {
        errors.planId = 'Select a plan';
      } else {
        errors.planId = 'No plan available for this type';
      }
    }
  }

  if (mode === 'existing_workspace') {
    if (!state.tenantId || !state.tenantId.trim()) {
      errors.tenantId = 'Select a workspace';
    }
  }

  if (mode === 'custom_workspace') {
    if (!state.tenantName.trim()) {
      errors.tenantName = 'Workspace name is required';
    }
    const planCode = resolveAssignedPlanCode(state.planType, state.fixedPlanCode, plans);
    if (!state.planType) {
      errors.planType = 'Select a plan type';
    } else if (!planCode) {
      if (state.planType === 'fixed') {
        errors.planId = 'Select a plan';
      } else {
        errors.planId = 'No plan available for this type';
      }
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Build minimal API payload for the given mode and state.
 * Only includes fields required by the backend for that case.
 */
export function buildInvitationPayload(
  mode: WorkspaceMode,
  state: InvitationFormState,
  plans: Plan[]
): InvitationPayload {
  const email = state.email.trim();
  const payload: InvitationPayload = { email };

  if (mode === 'new_workspace') {
    const code = resolveAssignedPlanCode(state.planType, state.fixedPlanCode, plans);
    if (code) payload.assigned_plan_code = code;
  }

  if (mode === 'existing_workspace') {
    if (state.tenantId?.trim()) payload.tenant_id = state.tenantId.trim();
  }

  if (mode === 'custom_workspace') {
    const name = state.tenantName.trim();
    if (name) payload.tenant_name = name;
    const code = resolveAssignedPlanCode(state.planType, state.fixedPlanCode, plans);
    if (code) payload.assigned_plan_code = code;
  }

  return payload;
}
